"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { partnerLogo } from "@/utils/partnerLogo";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

export default function PartnerSlider() {

  const [open, setOpen] = useState(false);


  const row1 = useMemo(() => partnerLogo.slice(1, 50), []);
  const row2 = useMemo(() => partnerLogo.slice(50, 100), []);
  const row3 = useMemo(() => partnerLogo.slice(100, 161), []);



  return (
    <div className="relative w-full overflow-hidden pt-6 md:pt-12">

      <div
        className="hidden sm:block absolute left-0 top-0 h-full w-24 z-20 pointer-events-none"
        style={{
          background:
            "linear-gradient(270deg, rgba(8,30,45,0) 0%, rgba(8,30,45,0.84) 50%, #081E2D 80%)",
        }}
      />
      <div
        className="hidden sm:block absolute right-0 top-0 h-full w-24 z-20 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(8,30,45,0) 0%, rgba(8,30,45,0.84) 50%, #081E2D 80%)",
        }}
      />

      <div className="overflow-hidden py-1">
        <div className="marquee w-max" style={{ "--speed": "340s" }}>
          {[...row1, ...row1].map((item, i) => (
            <Logo key={i} item={item} index={i} />
          ))}
        </div>
      </div>

      <div className="overflow-hidden py-1">
        <div className="marquee marquee-reverse w-max" style={{ "--speed": "341s" }}>
          {[...row2, ...row2].map((item, i) => (
            <Logo key={i} item={item} index={i + row1.length} />
          ))}
        </div>
      </div>

      <div className="overflow-hidden py-1">
        <div className="marquee w-max" style={{ "--speed": "338s" }}>
          {[...row3, ...row3].map((item, i) => (
            <Logo key={i} item={item} index={i + row1.length + row2.length} />
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center mt-10">
        <Link href={""} className="cursor-pointer flex items-center gap-2 text-[16px] font-semibold text-white"
          onClick={() => setOpen(true)}
        >
          <span className="text-lg">See All Partners</span>
          <div className="bg-[#FFA201] text-lg p-1 transition flex items-center justify-center">
            <span className="text-white  material-symbols-outlined">
              <ArrowRight />
            </span>
          </div>
        </Link>
      </div>
      <PartnersModal open={open} onClose={() => setOpen(false)} logos={partnerLogo} />
    </div>
  );
}

function Logo({ item }) {
  return (
    <div className="mx-1 flex-shrink-0">
      <div className="relative bg-white flex items-center justify-center shadow-sm"
        style={{ width: 220, height: 90 }}>
        <div className="relative w-full h-full">
          <Image
            src={item.img}
            alt={item.altText}
            fill
            sizes="(max-width: 640px) 120px, (max-width: 1024px) 160px, 220px"
            className="object-contain"
          />
        </div>
      </div>
    </div>
  );
}
function PartnersModal({ open, onClose, logos }) {
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState(false);

  // Mount/unmount with exit animation
  useEffect(() => {
    if (open) {
      setMounted(true);
      // next tick so transitions apply
      requestAnimationFrame(() => setShow(true));
    } else {
      setShow(false);
      const t = setTimeout(() => setMounted(false), 400); // match duration
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on ESC + lock scroll only when mounted
  useEffect(() => {
    if (!mounted) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [mounted, onClose]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* overlay */}
      <button
        aria-label="Close modal"
        onClick={onClose}
        className={[
          "absolute inset-0 bg-black/60",
          show ? "opacity-100" : "opacity-0",
        ].join(" ")}
        style={{
          transition: "opacity 400ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        type="button"
      />

      {/* panel wrapper (centering) */}
      <div className="relative w-full h-full flex items-start justify-center px-3">
        {/* modal panel */}
        <div
          className={[
            "relative mt-10 md:mt-16 w-[92%] max-w-6xl bg-white rounded-2xl shadow-xl overflow-hidden",
            "will-change-transform will-change-opacity",
            show ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-10 scale-90",
          ].join(" ")}
          style={{
            transition: "opacity 400ms cubic-bezier(0.16, 1, 0.3, 1), transform 400ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
          role="dialog"
          aria-modal="true"
          aria-label="All partners"
        >
          <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b">
            <div>
              <h3 className="text-lg md:text-xl font-bold text-slate-900">All Partners</h3>
              <p className="text-sm text-slate-600">
                Industry partners recruiting our graduates
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm"
            >
              Close
            </button>
          </div>

          <div className="p-4 md:p-6 max-h-[70vh] overflow-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
              {logos.slice(1).map((item, idx) => (
                <div
                  key={`${item.altText}-${idx}`}
                  className="bg-white border border-[#efefef] rounded-xl flex items-center justify-center"
                  style={{ minHeight: 84 }}
                >
                  <div className="relative w-full h-20">
                    <Image
                      src={item.img}
                      alt={item.altText}
                      fill
                      sizes="(max-width: 640px) 120px, (max-width: 1024px) 160px, 220px"
                      className="object-contain"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="px-4 md:px-6 py-4 border-t flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 text-sm"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );

}